import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ContagemDoCiclo,
  DetalheInventario as Detalhe,
  Divergencia,
  ItemDaContagem,
} from '../api/inventarios.js';
import { consultarInventario, tipoDaDiferenca } from '../api/inventarios.js';
import { useConsulta } from '../api/useConsulta.js';
import { Abas, PainelDeAba } from '../componentes/Abas.js';
import { Cartao } from '../componentes/Cartao.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import { formatarDataHora, formatarHora } from '../componentes/datas.js';
import { rotuloDoLocal } from '../componentes/deposito.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { IconeConfere, IconeInventarios } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import { CHIP_INVENTARIO, ROTULO_DA_DIFERENCA } from './inventarios/formato.js';
import './DetalheInventario.css';

const GRUPO_DE_ABAS = 'contagens';

/** Fundo da linha por sinal da diferença — a legenda do rodapé explica as cores. */
const CLASSE_POR_TIPO: Readonly<Record<string, string | undefined>> = {
  falta: 'tabela__linha--falta',
  sobra: 'tabela__linha--sobra',
  conferido: undefined,
};

/** Derivação vazia é o caso comum: o produto sem variação não tem o que mostrar. */
function derivacaoOuTraco(derivacao: string | null): string {
  return derivacao === null || derivacao.trim() === '' ? '—' : derivacao;
}

/**
 * O que o operador contou.
 *
 * Só código, derivação e quantidade: `ItemContagem` não guarda descrição de
 * produto, e a coluna "Descrição" que a tela exibia vinha inteira da fixture.
 */
const COLUNAS_DA_CONTAGEM: readonly Coluna<ItemDaContagem>[] = [
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '30%',
    celula: (item) => <span className="mono">{item.produto}</span>,
  },
  {
    chave: 'derivacao',
    rotulo: 'Derivação',
    largura: '20%',
    celula: (item) => derivacaoOuTraco(item.derivacao),
  },
  {
    chave: 'quantidade',
    rotulo: 'Quantidade',
    alinhamento: 'direita',
    celula: (item) => <span className="mono">{item.quantidade}</span>,
  },
];

/** A contagem confrontada com o saldo do ERP (RF-073). */
const COLUNAS_DA_COMPARACAO: readonly Coluna<Divergencia>[] = [
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '24%',
    celula: (divergencia) => <span className="mono">{divergencia.produto}</span>,
  },
  {
    chave: 'derivacao',
    rotulo: 'Derivação',
    largura: '14%',
    celula: (divergencia) => derivacaoOuTraco(divergencia.derivacao),
  },
  {
    chave: 'erp',
    rotulo: 'Saldo ERP',
    largura: '15%',
    alinhamento: 'direita',
    celula: (divergencia) => (
      <span className="mono detalhe-inventario__erp">{divergencia.saldoErp}</span>
    ),
  },
  {
    chave: 'contado',
    rotulo: 'Contado',
    largura: '15%',
    alinhamento: 'direita',
    celula: (divergencia) => <span className="mono">{divergencia.contado}</span>,
  },
  {
    chave: 'diferenca',
    rotulo: 'Diferença',
    largura: '16%',
    alinhamento: 'direita',
    // O sinal já vem do middleware; a tela só escolhe a cor por ele.
    celula: (divergencia) => (
      <span
        className={`detalhe-inventario__diferenca detalhe-inventario__diferenca--${tipoDaDiferenca(divergencia.diferenca)}`}
      >
        {divergencia.diferenca}
      </span>
    ),
  },
  {
    chave: 'tipo',
    rotulo: 'Situação',
    alinhamento: 'direita',
    celula: (divergencia) => {
      const tipo = tipoDaDiferenca(divergencia.diferenca);

      return tipo === 'conferido' ? (
        <span className="detalhe-inventario__conferido">
          <IconeConfere tamanho={15} />
          {ROTULO_DA_DIFERENCA.conferido}
        </span>
      ) : (
        <span className={`detalhe-inventario__situacao detalhe-inventario__situacao--${tipo}`}>
          {ROTULO_DA_DIFERENCA[tipo]}
        </span>
      );
    },
  },
];

/**
 * Um ciclo de inventário (UC13), pelo id da rota.
 *
 * São dois blocos porque são dois fatos diferentes: o que o operador contou, e
 * a comparação dessa contagem com o saldo do ERP. O segundo depende do conector
 * do Senior, que ainda não existe — e por isso ele diz que a comparação não
 * aconteceu, em vez de abrir uma tabela vazia que se leria como "tudo conferido".
 *
 * Resolver divergência e aceitar contagem (RF-063, RF-074) são ESCRITA e não
 * têm rota no middleware: os botões saíram da tela em vez de fingir decisão.
 */
export function DetalheInventario() {
  const { id } = useParams();
  const [contagemEscolhida, definirContagemEscolhida] = useState<string | null>(null);

  const buscar = useMemo(() => (id === undefined ? null : () => consultarInventario(id)), [id]);
  const consulta = useConsulta(buscar);
  const detalhe = consulta.dados;

  const contagem = contagemVisivel(detalhe, contagemEscolhida);

  return (
    <Pagina
      voltarPara="/inventarios"
      contexto={contexto(detalhe, contagem)}
      titulo={
        detalhe === null ? (
          'Ciclo de inventário'
        ) : (
          <>
            <span className="mono detalhe-inventario__local">
              {rotuloDoLocal(detalhe.codFil, detalhe.depositoErp)}
            </span>
            <ChipStatus
              tom={CHIP_INVENTARIO[detalhe.situacao].tom}
              rotulo={CHIP_INVENTARIO[detalhe.situacao].rotulo}
              tamanho="tabela"
            />
          </>
        )
      }
      acoes={
        detalhe !== null && (
          <>
            <Metrica
              rotulo="SKUs contados"
              valor={contagem === null ? '—' : String(contagem.totalSkus)}
            />
            <Metrica rotulo="Unidades" valor={contagem?.totalUnidades ?? '—'} />
            {/*
              Soma das diferenças com sinal: negativo é falta física no armazém.
              Sem divergência gravada não houve comparação com o ERP, e a soma de
              zero linhas volta "0" do middleware — anunciá-lo aqui afirmaria que
              o depósito fecha, exatamente o que o cartão de comparação desmente
              logo abaixo. Volta quando a comparação existir (F2.3).
            */}
            {detalhe.totais.divergenciasTotal > 0 && (
              <Metrica
                rotulo="Saldo líquido"
                valor={detalhe.totais.saldoLiquido}
                tom={detalhe.totais.saldoLiquido.startsWith('-') ? 'erro' : 'neutro'}
              />
            )}
            {detalhe.contagens.length > 1 && (
              <Link className="detalhe-inventario__conflito" to="conflito">
                Ver conflito de contagem
              </Link>
            )}
          </>
        )
      }
    >
      <Resultado
        consulta={consulta}
        espera="Carregando o inventário…"
        estaVazio={() => false}
        vazio={null}
      >
        {(dados) => (
          <>
            <BlocoDaContagem
              detalhe={dados}
              contagem={contagem}
              aoEscolher={definirContagemEscolhida}
            />
            <BlocoDaComparacao divergencias={dados.divergencias} />
          </>
        )}
      </Resultado>
    </Pagina>
  );
}

/**
 * Qual contagem a tela mostra.
 *
 * A primeira em ordem cronológica do APARELHO, e não a "melhor": eleger uma
 * entre contagens concorrentes seria resolver o conflito por conta própria
 * (RF-063). Quem troca é quem está lendo, pelas abas.
 */
function contagemVisivel(
  detalhe: Detalhe | null,
  escolhida: string | null,
): ContagemDoCiclo | null {
  if (detalhe === null || detalhe.contagens.length === 0) {
    return null;
  }

  return detalhe.contagens.find((item) => item.id === escolhida) ?? detalhe.contagens[0] ?? null;
}

/** Procedência da contagem em uma linha: quem, em qual aparelho, e os dois relógios. */
function contexto(detalhe: Detalhe | null, contagem: ContagemDoCiclo | null): string {
  if (detalhe === null) {
    return 'Inventários';
  }

  const partes = [
    'Inventários',
    `aberto em ${formatarDataHora(detalhe.abertoEm)}`,
    contagem?.operador?.nome,
    contagem?.dispositivo?.identificador,
  ];

  return partes.filter((parte) => parte !== undefined).join(' · ');
}

function BlocoDaContagem({
  detalhe,
  contagem,
  aoEscolher,
}: {
  readonly detalhe: Detalhe;
  readonly contagem: ContagemDoCiclo | null;
  readonly aoEscolher: (id: string) => void;
}) {
  const abas = useMemo(
    () =>
      detalhe.contagens.map((item, indice) => ({
        id: item.id,
        rotulo: `Contagem ${String(indice + 1)}`,
        contador: item.totalSkus,
      })),
    [detalhe.contagens],
  );

  const trocar = useCallback(
    (id: string) => {
      aoEscolher(id);
    },
    [aoEscolher],
  );

  if (contagem === null) {
    return (
      <Cartao titulo="Contagem física" semRespiro>
        <EstadoVazio
          tom="neutro"
          icone={<IconeInventarios tamanho={28} />}
          titulo="Nenhuma contagem registrada neste ciclo."
          detalhe="O ciclo está aberto e ainda espera a contagem de um coletor."
        />
      </Cartao>
    );
  }

  return (
    <Cartao
      titulo="Contagem física"
      acessorio={
        detalhe.contagens.length > 1 && (
          <Abas
            rotulo="Contagens do ciclo"
            abas={abas}
            ativa={contagem.id}
            aoTrocar={trocar}
            grupo={GRUPO_DE_ABAS}
          />
        )
      }
      semRespiro
    >
      <PainelDeAba id={contagem.id} grupo={GRUPO_DE_ABAS}>
        <p className="detalhe-inventario__procedencia">
          {contagem.operador?.nome ?? 'Operador não identificado'}
          {contagem.dispositivo !== null && ` · ${contagem.dispositivo.identificador}`}
          {/* Os dois relógios: a distância entre eles é o tempo em modo avião. */}
          {` · contado ${formatarHora(contagem.registradoEm)}`}
          {` · recebido ${formatarHora(contagem.recebidaEm)}`}
        </p>
        <Tabela
          itens={contagem.itens}
          colunas={COLUNAS_DA_CONTAGEM}
          chaveDe={(item) => item.id}
          descricao={`Itens contados em ${rotuloDoLocal(detalhe.codFil, detalhe.depositoErp)}`}
          densidade="confortavel"
        />
      </PainelDeAba>
    </Cartao>
  );
}

function BlocoDaComparacao({ divergencias }: { readonly divergencias: readonly Divergencia[] }) {
  if (divergencias.length === 0) {
    return (
      <Cartao titulo="Comparação com o ERP" semRespiro>
        <EstadoVazio
          tom="neutro"
          icone={<IconeInventarios tamanho={28} />}
          titulo="A comparação com o saldo do ERP ainda não aconteceu."
          detalhe="Ela depende da integração com o Senior. Enquanto ela não existir, não há saldo do ERP para confrontar com esta contagem — e isto não significa que o depósito está conferido."
        />
      </Cartao>
    );
  }

  return (
    <Cartao titulo="Comparação com o ERP" semRespiro>
      <Tabela
        itens={divergencias}
        colunas={COLUNAS_DA_COMPARACAO}
        chaveDe={(divergencia) => divergencia.id}
        descricao="Contagem física comparada ao saldo do ERP"
        densidade="confortavel"
        classeDaLinha={(divergencia) => CLASSE_POR_TIPO[tipoDaDiferenca(divergencia.diferenca)]}
        rodape={
          <div className="detalhe-inventario__legenda">
            <span className="detalhe-inventario__item-legenda">
              <span className="detalhe-inventario__amostra detalhe-inventario__amostra--falta" />
              falta física
            </span>
            <span className="detalhe-inventario__item-legenda">
              <span className="detalhe-inventario__amostra detalhe-inventario__amostra--sobra" />
              sobra física
            </span>
            <span className="detalhe-inventario__nota">
              A decisão sobre a divergência — aceitar a contagem, manter o ERP ou recontar — é
              humana (RF-063) e ainda não é registrada pelo painel.
            </span>
          </div>
        }
      />
    </Cartao>
  );
}

function Metrica({
  rotulo,
  valor,
  tom = 'neutro',
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly tom?: 'neutro' | 'erro';
}) {
  return (
    <span className="detalhe-inventario__metrica">
      <span className="detalhe-inventario__metrica-rotulo">{rotulo}</span>
      <span
        className={`detalhe-inventario__metrica-valor detalhe-inventario__metrica-valor--${tom}`}
      >
        {valor}
      </span>
    </span>
  );
}
